import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, firstValueFrom } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { FirestoreTosite, PaymentStatus } from '@lemon/core-firebase/model/tosite'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { TositeHakuStateService } from 'app/_angular/service/tosite/tosite-haku-state.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { OstolaskutSelaaDataSourceService } from './ostolaskut-selaa.datasource.service'

interface TilaVaihtoehto {
  tunnus: PaymentStatus | 'kaikki'
  nimi: string
}

interface TilaLomake {
  tila: PaymentStatus | 'kaikki'
}

@Component({
  templateUrl: './ostolaskut-selaa.component.html',
  styleUrls: ['./ostolaskut-selaa.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [OstolaskutSelaaDataSourceService],
})
export class OstolaskutSelaaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  readonly asiakas = signal<Asiakas | null>(null)
  hakuAvoinna = false

  lomake: FieldTree<TilaLomake>

  rivienMaaraObservable: Observable<number>
  lataaObservable: Observable<boolean>
  rivitObservable: Observable<FirestoreTosite[]>

  displayedColumns = ['paymentStatus', 'seller', 'p', 'dueDate', 'paymentDate', 'laskunumero', 'sum']

  // Näytettävät tilat — sisältää 'kaikki' + PaymentStatus-arvot maksut-näkymän tapaan
  tilavaihtoehdot: TilaVaihtoehto[] = [
    { tunnus: 'kaikki', nimi: 'Kaikki' },
    { tunnus: PaymentStatus.AVOIN, nimi: 'Avoin' },
    { tunnus: PaymentStatus.ERAANTYNYT, nimi: 'Erääntynyt' },
    { tunnus: PaymentStatus.MAKSUSSA, nimi: 'Maksussa' },
    { tunnus: PaymentStatus.MAKSETTU, nimi: 'Maksettu' },
    { tunnus: PaymentStatus.MAKSETTU_TOISAALLA, nimi: 'Maksettu toisaalla' },
    { tunnus: PaymentStatus.PANKKI_HYLKASI, nimi: 'Pankki hylkäsi' },
    { tunnus: PaymentStatus.ASIAKAS_HYLKASI, nimi: 'Asiakas hylkäsi' },
    { tunnus: PaymentStatus.KASITELTY, nimi: 'Käsitelty' },
  ]

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _hakuStateService: TositeHakuStateService,
    public dataSource: OstolaskutSelaaDataSourceService,
  ) {
    this.rivienMaaraObservable = this.dataSource.rivienMaaraObservable
    this.lataaObservable = this.dataSource.lataaObservable
    this.rivitObservable = this.dataSource.nakyvatObservable
  }

  avaaHaku() {
    this.hakuAvoinna = true
  }

  suljeHaku() {
    this.hakuAvoinna = false
  }

  ngOnInit() {
    if (this._hakuStateService.onkoHakutilaTallennettu(this._router.url)) {
      this.hakuAvoinna = true
    }

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })

    this.lomake = form(signal<TilaLomake>({ tila: 'kaikki' }), { injector: this._injector })

    arvonMuutokset(this.lomake.tila, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(arvo => {
        this.dataSource.setTila(arvo ?? 'kaikki')
      })
  }

  ngAfterViewInit() {
    const initial = this.dataSource.sort
    this.sort.sort({ id: initial.active, start: initial.direction === 'asc' ? 'asc' : 'desc', disableClear: true })
    this.sort.sortChange.pipe(takeUntil(this._ngUnsubscribe)).subscribe(sort => this.dataSource.setSort(sort))
    this.paginator.page.pipe(takeUntil(this._ngUnsubscribe)).subscribe(page => this.dataSource.setPage(page))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  /** Vain vaihteluvälissä olevat PaymentStatus-arvot tunnistetaan väriä varten. */
  paymentStatusLuokka(t: FirestoreTosite): string {
    switch (t.paymentStatus) {
      case PaymentStatus.AVOIN:
        return 'blue-text'
      case PaymentStatus.ERAANTYNYT:
      case PaymentStatus.PANKKI_HYLKASI:
      case PaymentStatus.ASIAKAS_HYLKASI:
        return 'red-text'
      case PaymentStatus.MAKSETTU:
      case PaymentStatus.MAKSETTU_TOISAALLA:
        return 'green-text'
      case PaymentStatus.MAKSUSSA:
        return 'mint-text'
      default:
        return ''
    }
  }

  paymentStatusNimi(status: PaymentStatus | undefined): string {
    const found = this.tilavaihtoehdot.find(t => t.tunnus === status)
    return found ? found.nimi : '–'
  }

  async avaa(rivi: FirestoreTosite) {
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!avainTiedot?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'ostolaskut', 'selaa', rivi.avain])
  }
}
